import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import EnglishDatePicker from '../../components/EnglishDatePicker/EnglishDatePicker';
import '../Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://metro4.dcairo.com/api';

const AddBreakingResults = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [todaysPours, setTodaysPours] = useState([]);
  const [selectedAge, setSelectedAge] = useState('');
  const [filteredPours, setFilteredPours] = useState([]);
  const [testingDate, setTestingDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [allResults, setAllResults] = useState([]);
  
  const [signatures, setSignatures] = useState({
    ocLab: { name: '', date: '' },
    thirdParty: { name: '', date: '' },
    noce: { name: '', date: '' }
  });
  
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    fetchTodaysBreaking();
  }, []);

  useEffect(() => {
    if (selectedAge && todaysPours.length > 0) {
      const filtered = todaysPours.filter(p => p.age === parseInt(selectedAge));
      setFilteredPours(filtered);
      initializeResults(filtered);
    } else {
      setFilteredPours([]);
      setAllResults([]);
    }
  }, [selectedAge, todaysPours]);

  const fetchTodaysBreaking = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      
      const response = await axios.get(`${API_URL}/pours/todays-breaking?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTodaysPours(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching todays breaking:', error);
      if (error.response?.status === 401) {
        toast.error('انتهت الجلسة');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const initializeResults = (pours) => {
    const results = [];
    let sn = 1;
    
    pours.forEach((pour) => {
      const groupsCount = pour.cubesCount?.groups || 0;
      
      for (let i = 0; i < groupsCount; i++) {
        results.push({
          pourId: pour.pourId,
          station: pour.station,
          location: pour.location,
          mix: pour.mix,
          batchPlant: pour.batchPlant,
          castingDate: pour.castingDate,
          group: i + 1,
          sn1: sn++,
          sn2: sn++,
          cube1: { weight: '', density: '', load: '', strength: '' },
          cube2: { weight: '', density: '', load: '', strength: '' },
          groupAverage: 0,
          overallAverage: 0,
          note: ''
        });
      }
    });
    
    setAllResults(results);
  };

  const calculateStrength = (load) => {
    if (!load || load === '') return '';
    const loadNum = parseFloat(load);
    const area = 150 * 150;
    const strength = (loadNum * 1000) / area;
    return strength.toFixed(2);
  };

  const calculateDensity = (weight) => {
    if (!weight || weight === '') return '';
    const weightNum = parseFloat(weight);
    const volume = 150 * 150 * 150;
    const density = (weightNum * 1000000) / volume;
    return density.toFixed(2);
  };

  const handleCubeChange = (resultIndex, cubeNumber, field, value) => {
    const newResults = [...allResults];
    const cube = cubeNumber === 1 ? 'cube1' : 'cube2';
    
    newResults[resultIndex][cube][field] = value;
    
    if (field === 'weight') {
      newResults[resultIndex][cube].density = calculateDensity(value);
    }
    
    if (field === 'load') {
      newResults[resultIndex][cube].strength = calculateStrength(value);
    }
    
    const strength1 = parseFloat(newResults[resultIndex].cube1.strength) || 0;
    const strength2 = parseFloat(newResults[resultIndex].cube2.strength) || 0;
    newResults[resultIndex].groupAverage = ((strength1 + strength2) / 2).toFixed(2);
    
    const validAverages = newResults
      .map(r => parseFloat(r.groupAverage))
      .filter(avg => avg > 0);
    const overallAvg = validAverages.length > 0 
      ? (validAverages.reduce((a, b) => a + b, 0) / validAverages.length).toFixed(2)
      : 0;
    
    newResults.forEach(r => r.overallAverage = overallAvg);
    
    setAllResults(newResults);
  };

  const handleNoteChange = (resultIndex, value) => {
    const newResults = [...allResults];
    newResults[resultIndex].note = value;
    setAllResults(newResults);
  };

  const handleSignatureChange = (type, field, value) => {
    setSignatures(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const formatDateForAPI = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSaveResults = async () => {
    if (!selectedAge) {
      toast.error('اختر عمر الاختبار أولاً');
      return;
    }

    if (allResults.length === 0) {
      toast.error('لا توجد بيانات للحفظ');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const pourGroups = {};
      allResults.forEach(result => {
        if (!pourGroups[result.pourId]) {
          pourGroups[result.pourId] = [];
        }
        pourGroups[result.pourId].push(result);
      });

      for (const pourId in pourGroups) {
        const pourResults = pourGroups[pourId];
        
        const payload = {
          age: selectedAge,
          testingDate: testingDate,
          results: pourResults,
          signatures: signatures
        };

        await axios.post(
          `${API_URL}/pours/${pourId}/breaking-results`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('تم حفظ جميع النتائج بنجاح');
      navigate('/cubes/results');
      
    } catch (error) {
      console.error('Error saving results:', error);
      toast.error(error.response?.data?.message || 'فشل حفظ النتائج');
    } finally {
      setLoading(false);
    }
  };

  const totalCubes = allResults.length * 2;

  return (
    <div className="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <button className="menu-toggle" onClick={toggleSidebar}>
            ☰
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/cubes/results')}
              style={{
                background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '700',
                transition: 'all 0.3s',
                boxShadow: '0 4px 12px rgba(100, 116, 139, 0.3)'
              }}
            >
              ← رجوع
            </button>
            <h1 style={{ margin: 0 }}>➕ إضافة نتائج التكسير</h1>
          </div>
        </div>

        <div className="dashboard-main">
          <style>{`
            .add-results-container {
              max-width: 1600px;
              margin: 0 auto;
              padding: 20px;
            }

            .age-selection {
              background: white;
              padding: 30px;
              border-radius: 12px;
              margin-bottom: 20px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }

            .age-title {
              font-size: 20px;
              font-weight: 700;
              margin: 0 0 20px 0;
              color: #1e293b;
            }

            .age-options {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 15px;
            }

            .age-option {
              padding: 20px;
              border: 3px solid #e2e8f0;
              border-radius: 12px;
              text-align: center;
              cursor: pointer;
              transition: all 0.3s;
              font-size: 18px;
              font-weight: 700;
            }

            .age-option:hover {
              border-color: #3b82f6;
              background: #eff6ff;
            }

            .age-option.selected {
              border-color: #16a34a;
              background: #f0fdf4;
              color: #16a34a;
            }

            .pours-summary {
              background: #eff6ff;
              padding: 15px;
              border-radius: 8px;
              margin-bottom: 20px;
              border-left: 4px solid #3b82f6;
            }

            .summary-text {
              font-size: 14px;
              font-weight: 600;
              color: #1e40af;
              margin: 5px 0;
            }

            .paper-container {
              background: white;
              padding: 30px;
              border-radius: 12px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }

            .paper-header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 3px solid #000;
              padding-bottom: 15px;
            }

            .paper-header h2 {
              font-size: 24px;
              font-weight: 800;
              margin: 0;
            }

            .info-section {
              border: 2px solid #000;
              margin-bottom: 20px;
            }

            .info-row {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              border-bottom: 2px solid #000;
            }

            .info-row:last-child {
              border-bottom: none;
            }

            .info-cell {
              padding: 10px;
              border-right: 2px solid #000;
              font-size: 14px;
            }

            .info-cell:last-child {
              border-right: none;
            }

            .info-label {
              font-weight: 700;
              margin-right: 5px;
            }

            .results-table {
              width: 100%;
              border-collapse: collapse;
              border: 2px solid #000;
              margin-bottom: 20px;
            }

            .results-table th,
            .results-table td {
              border: 1px solid #000;
              padding: 6px 3px;
              text-align: center;
              font-size: 10px;
            }

            .results-table th {
              background: #f0f0f0;
              font-weight: 700;
              font-size: 9px;
            }

            .group-cell {
              font-weight: 700;
              background: #f8f8f8;
            }

            .table-input {
              width: 100%;
              border: 1px solid #e2e8f0;
              text-align: center;
              font-size: 10px;
              padding: 3px;
              border-radius: 3px;
            }

            .table-input:focus {
              outline: 2px solid #3b82f6;
            }

            .readonly-input {
              background: #f8f8f8;
            }

            .signatures-section {
              border: 2px solid #000;
              margin-bottom: 20px;
            }

            .signatures-grid {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
            }

            .signature-col {
              border-right: 2px solid #000;
              text-align: center;
            }

            .signature-col:last-child {
              border-right: none;
            }

            .signature-header {
              padding: 10px;
              font-weight: 700;
              font-size: 13px;
              border-bottom: 2px solid #000;
              background: #f0f0f0;
            }

            .signature-row {
              padding: 10px;
              border-bottom: 1px solid #000;
              min-height: 40px;
            }

            .signature-row:last-child {
              border-bottom: none;
            }

            .signature-input {
              width: 90%;
              border: 1px solid #e2e8f0;
              text-align: center;
              font-size: 12px;
              padding: 6px;
              border-radius: 4px;
            }

            .action-buttons {
              display: flex;
              gap: 10px;
              justify-content: center;
              margin-top: 20px;
            }

            .btn-save {
              background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
              color: white;
              border: none;
              padding: 12px 30px;
              border-radius: 10px;
              font-size: 16px;
              font-weight: 700;
              cursor: pointer;
              transition: all 0.3s;
            }

            .btn-save:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(22, 163, 74, 0.4);
            }

            .btn-cancel {
              background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
              color: white;
              border: none;
              padding: 12px 30px;
              border-radius: 10px;
              font-size: 16px;
              font-weight: 700;
              cursor: pointer;
              transition: all 0.3s;
            }

            .btn-cancel:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
            }

            .empty-state {
              text-align: center;
              padding: 60px 20px;
              color: #94a3b8;
            }

            @media (max-width: 768px) {
              .info-row {
                grid-template-columns: 1fr;
              }

              .info-cell {
                border-right: none;
                border-bottom: 1px solid #000;
              }

              .results-table th,
              .results-table td {
                font-size: 8px;
                padding: 3px 2px;
              }

              .signatures-grid {
                grid-template-columns: 1fr;
              }

              .signature-col {
                border-right: none;
                border-bottom: 2px solid #000;
              }
            }
          `}</style>

          <div className="add-results-container">
            <div className="age-selection">
              <h3 className="age-title">🎯 اختر عمر الاختبار:</h3>
              <div className="age-options">
                <div
                  className={`age-option ${selectedAge === '7' ? 'selected' : ''}`}
                  onClick={() => setSelectedAge('7')}
                >
                  7 أيام
                </div>
                <div
                  className={`age-option ${selectedAge === '14' ? 'selected' : ''}`}
                  onClick={() => setSelectedAge('14')}
                >
                  14 يوم
                </div>
                <div
                  className={`age-option ${selectedAge === '28' ? 'selected' : ''}`}
                  onClick={() => setSelectedAge('28')}
                >
                  28 يوم
                </div>
                <div
                  className={`age-option ${selectedAge === '56' ? 'selected' : ''}`}
                  onClick={() => setSelectedAge('56')}
                >
                  56 يوم
                </div>
              </div>
            </div>

            {loading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
                <p>جاري التحميل...</p>
              </div>
            )}

            {!loading && selectedAge && filteredPours.length === 0 && (
              <div className="empty-state">
                <div style={{ fontSize: '60px', marginBottom: '20px' }}>🔭</div>
                <h3 style={{ fontSize: '20px', color: '#64748b' }}>
                  لا توجد مكعبات للتكسير اليوم بعمر {selectedAge} يوم
                </h3>
              </div>
            )}

            {selectedAge && filteredPours.length > 0 && (
              <>
                <div className="pours-summary">
                  <div className="summary-text">
                    📊 عدد الصبات: {filteredPours.length}
                  </div>
                  <div className="summary-text">
                    🧱 إجمالي المكعبات: {totalCubes} مكعب
                  </div>
                  <div className="summary-text">
                    📍 المحطات: {[...new Set(filteredPours.map(p => p.station))].join(' - ')}
                  </div>
                </div>

                <div className="paper-container">
                  <div className="paper-header">
                    <h2>🗝️ نتائج اختبار التكسير - {selectedAge} يوم</h2>
                  </div>

                  <div className="info-section">
                    <div className="info-row">
                      <div className="info-cell">
                        <span className="info-label">Testing Date:</span>
                        <EnglishDatePicker
                          selected={testingDate ? new Date(testingDate) : null}
                          onChange={(date) => setTestingDate(formatDateForAPI(date))}
                        />
                      </div>
                      <div className="info-cell">
                        <span className="info-label">Age:</span>
                        {selectedAge} days
                      </div>
                      <div className="info-cell">
                        <span className="info-label">Total Cubes:</span>
                        {totalCubes}
                      </div>
                    </div>
                  </div>

                  <table className="results-table">
                    <thead>
                      <tr>
                        <th rowSpan="2">SN</th>
                        <th rowSpan="2">Station</th>
                        <th rowSpan="2">Element</th>
                        <th rowSpan="2">Mix</th>
                        <th rowSpan="2">Group</th>
                        <th rowSpan="2">Weight<br/>(gm)</th>
                        <th rowSpan="2">Density<br/>(gm/cm³)</th>
                        <th rowSpan="2">Load<br/>(KN)</th>
                        <th rowSpan="2">Strength<br/>(MPa)</th>
                        <th colSpan="2">Average</th>
                        <th rowSpan="2">Note</th>
                      </tr>
                      <tr>
                        <th>(MPa)</th>
                        <th>(MPa)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allResults.map((result, index) => (
                        <React.Fragment key={index}>
                          <tr>
                            <td>{result.sn1}</td>
                            <td rowSpan="2">{result.station}</td>
                            <td rowSpan="2">{result.location}</td>
                            <td rowSpan="2">{result.mix}</td>
                            <td rowSpan="2" className="group-cell">G{result.group}</td>
                            <td>
                              <input
                                type="number"
                                className="table-input"
                                value={result.cube1.weight}
                                onChange={(e) => handleCubeChange(index, 1, 'weight', e.target.value)}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="table-input readonly-input"
                                value={result.cube1.density}
                                readOnly
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="table-input"
                                value={result.cube1.load}
                                onChange={(e) => handleCubeChange(index, 1, 'load', e.target.value)}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="table-input readonly-input"
                                value={result.cube1.strength}
                                readOnly
                              />
                            </td>
                            <td rowSpan="2" style={{ fontWeight: '700', fontSize: '11px' }}>
                              {result.groupAverage || '-'}
                            </td>
                            <td rowSpan="2" style={{ fontWeight: '700', fontSize: '11px' }}>
                              {result.overallAverage || '-'}
                            </td>
                            <td rowSpan="2">
                              <input
                                type="text"
                                className="table-input"
                                value={result.note}
                                onChange={(e) => handleNoteChange(index, e.target.value)}
                                placeholder="ملاحظات"
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>{result.sn2}</td>
                            <td>
                              <input
                                type="number"
                                className="table-input"
                                value={result.cube2.weight}
                                onChange={(e) => handleCubeChange(index, 2, 'weight', e.target.value)}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="table-input readonly-input"
                                value={result.cube2.density}
                                readOnly
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="table-input"
                                value={result.cube2.load}
                                onChange={(e) => handleCubeChange(index, 2, 'load', e.target.value)}
                                placeholder="0"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="table-input readonly-input"
                                value={result.cube2.strength}
                                readOnly
                              />
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>

                  <div className="signatures-section">
                    <div className="signatures-grid">
                      <div className="signature-col">
                        <div className="signature-header">OC Lab. Engineer</div>
                        <div className="signature-row">
                          <div style={{ marginBottom: '5px', fontWeight: '700', fontSize: '12px' }}>Name</div>
                          <input 
                            type="text" 
                            className="signature-input"
                            value={signatures.ocLab.name}
                            onChange={(e) => handleSignatureChange('ocLab', 'name', e.target.value)}
                            placeholder="الاسم"
                          />
                        </div>
                        <div className="signature-row">
                          <div style={{ fontWeight: '700', fontSize: '12px' }}>Signature</div>
                        </div>
                        <div className="signature-row">
                          <div style={{ marginBottom: '5px', fontWeight: '700', fontSize: '12px' }}>Date</div>
                          <EnglishDatePicker
                            selected={signatures.ocLab.date ? new Date(signatures.ocLab.date) : null}
                            onChange={(date) => handleSignatureChange('ocLab', 'date', formatDateForAPI(date))}
                          />
                        </div>
                      </div>

                      <div className="signature-col">
                        <div className="signature-header">4TL (Third Party)</div>
                        <div className="signature-row">
                          <div style={{ marginBottom: '5px', fontWeight: '700', fontSize: '12px' }}>Name</div>
                          <input 
                            type="text" 
                            className="signature-input"
                            value={signatures.thirdParty.name}
                            onChange={(e) => handleSignatureChange('thirdParty', 'name', e.target.value)}
                            placeholder="الاسم"
                          />
                        </div>
                        <div className="signature-row">
                          <div style={{ fontWeight: '700', fontSize: '12px' }}>Signature</div>
                        </div>
                        <div className="signature-row">
                          <div style={{ marginBottom: '5px', fontWeight: '700', fontSize: '12px' }}>Date</div>
                          <EnglishDatePicker
                            selected={signatures.thirdParty.date ? new Date(signatures.thirdParty.date) : null}
                            onChange={(date) => handleSignatureChange('thirdParty', 'date', formatDateForAPI(date))}
                          />
                        </div>
                      </div>

                      <div className="signature-col">
                        <div className="signature-header">NOCE/NAT</div>
                        <div className="signature-row">
                          <div style={{ marginBottom: '5px', fontWeight: '700', fontSize: '12px' }}>Name</div>
                          <input 
                            type="text" 
                            className="signature-input"
                            value={signatures.noce.name}
                            onChange={(e) => handleSignatureChange('noce', 'name', e.target.value)}
                            placeholder="الاسم"
                          />
                        </div>
                        <div className="signature-row">
                          <div style={{ fontWeight: '700', fontSize: '12px' }}>Signature</div>
                        </div>
                        <div className="signature-row">
                          <div style={{ marginBottom: '5px', fontWeight: '700', fontSize: '12px' }}>Date</div>
                          <EnglishDatePicker
                            selected={signatures.noce.date ? new Date(signatures.noce.date) : null}
                            onChange={(date) => handleSignatureChange('noce', 'date', formatDateForAPI(date))}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="action-buttons">
                    <button className="btn-cancel" onClick={() => navigate('/cubes/results')}>
                      إلغاء
                    </button>
                    <button className="btn-save" onClick={handleSaveResults} disabled={loading}>
                      {loading ? 'جاري الحفظ...' : '💾 حفظ النتائج'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBreakingResults;